import React, { useEffect, useRef, useState } from "react";
import "./styles.css";
import PATHS from "../../Routes/paths";
import {
  AutoComplete,
  Breadcrumb,
  SuccessPopupModal,
} from "../../Components/Atoms";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ALERT_VEHICLE_TABLE_HEADERS } from "../../Library/tableHeaders";
import { Button } from "@chakra-ui/react";
import { OVERSPEED_TRIGGER_TYPE, SPEED_ALERTS_TITLE } from "../../Library/constants";
import { saveAlert, updateAlert } from "../../APIs";
import { isEqual } from "lodash";
import { AlertsVehiclesTable } from "../../Components/Organisms";

const SetOverSpeedAlert = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const formInitialState = {
    sl: "",
    stln: null,
    selectedVehicles: [],
  };
  const [formData, setFormData] = useState(formInitialState);
  const [isOpen, setIsOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const SelectedVids = useRef([]);
  const [isEdit, setIsEdit] = useState(false);
  const [triggerRender, setTriggerRender] = useState(false);
  const initialState = useRef({});

  const setFormFieldData = (key) => (value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrMsg("");
  };

  const speedLimitOptions = [
    { value: 1, label: "For More Than 10 seconds", seconds: 10 },
    { value: 2, label: "For More Than 1 minutes", seconds: 60 },
    { value: 3, label: "For More Than 3 minutes", seconds: 60 * 3 },
    { value: 4, label: "For More Than 5 minutes", seconds: 60 * 5 },
  ];

  const breadcrumbPaths = [
    {
      idx: 1,
      name: "Overspeed Alerts",
      link: `${PATHS.SET_ALERTS}?tab=${2}`,
    },
    { idx: 2, name: isEdit ? "Edit Overspeed Alerts" : "Set Overspeed Alerts", isCurrentPage: true },
  ];

  const submitHandler = () => {
    if (!formData["sl"]) return setErrMsg("Please enter overspeed limit");
    else if (!formData["stln"]) return setErrMsg("Please select time duration");
    else if (!SelectedVids.current || !SelectedVids.current.length) return setErrMsg("Please select vehicles for this trigger");

    let data = {
      title: SPEED_ALERTS_TITLE,
      spdLmt: parseInt(formData["sl"]),
      typ: OVERSPEED_TRIGGER_TYPE,
      ts: Date.now(),
      vehIdArr: SelectedVids.current,
    }

    let selectedSpdLmtDur = speedLimitOptions.find(lmt => lmt.value === formData["stln"]);
    if (selectedSpdLmtDur && selectedSpdLmtDur.seconds) data.thrshld = (selectedSpdLmtDur.seconds * 1000);
    else return setErrMsg("Please select valid time duration");

    saveAlert(data, (err) => {
      if (err) setErrMsg(err);
      else setIsOpen(true);
    });
  }

  const handleRowSelect = (_, selected) => {
    if (selected && selected.length) {
      SelectedVids.current = selected.map(selection => selection.original.idx);
    }
  }

  const saveHandler = () => {
    if (!formData["sl"]) return setErrMsg("Please enter overspeed limit");
    else if (!formData["stln"]) return setErrMsg("Please select time duration");
    else if (!SelectedVids.current || !SelectedVids.current.length) return setErrMsg("Please select vehicles for this trigger");

    let idx = searchParams.get("idx");
    let data = { typ: OVERSPEED_TRIGGER_TYPE };

    if (idx && Number(idx)) data.idx = Number(idx);

    if(!isEqual(initialState.current.vehIdArr, SelectedVids.current)){
      data.vehIdArr = SelectedVids.current;
    }

    if(initialState.current.spdLmt !== parseInt(formData["sl"])){
      data.spdLmt = parseInt(formData["sl"]);
    }

    const speedLimitOption = speedLimitOptions.find(lmt => lmt.value === formData["stln"]);
    if(initialState.current.stln !== speedLimitOption.value){
      data.thrshld = speedLimitOption.seconds * 1000;
    }

    if(Object.keys(data).length === 1) return setErrMsg("No Changes Made to Save !!");

    updateAlert(data, (err) => {
      if(err) {
        setErrMsg("Error Saving Overspeed Alert");
        console.error("Error Saving Overspeed Alert", err);
      } else setIsOpen(true);
    })
  }

  useEffect(() => {
    const edit = searchParams.get("edit");
    const idx = searchParams.get("idx");

    if (edit && idx) {
      setIsEdit(true);
      setFormData({
        sl: location.state.spdLmt,
        stln: speedLimitOptions.find(lmt => lmt.seconds === location.state.threshold / 1000)?.value || null,
        selectedVehicles: location.state.vehIdArr,
      });

      SelectedVids.current = location.state.vehIdArr;

      initialState.current = {
        spdLmt: location.state.spdLmt,
        stln: speedLimitOptions.find(lmt => lmt.seconds === location.state.threshold / 1000)?.value || null,
        vehIdArr: location.state.vehIdArr,
      };

      setTriggerRender((prev) => !prev);
    }

  }, []);


  return (
    <div className="set-os-alert">
      <div className="set-os-alert-breadcrumb">
        <Breadcrumb paths={breadcrumbPaths} />
      </div>
      <div className="set-os-alert-form">
        <form className="set-os-form">
          <div className="set-os-form-field-cnt">
            <div className="set-os-title-container">
              <span>Set Overspeed Alert</span>
              <hr />
            </div>
            <div className="set-os-input-fields">
              <label className="kms-input-label">
                <p>When speed exceeds</p>
                <input placeholder="0" type="number" value={formData["sl"]} onChange={(event) => setFormFieldData("sl")(event.target.value)} />
                <p>km/hr</p>
              </label>
              <AutoComplete
                autoCompleteClassName={"add-client-autocomplete"}
                className={`add-client-autocomplete-field`}
                labelClassName={"add-client-autocomplete-field-label"}
                options={speedLimitOptions}
                inputName={"timeDuration"}
                placeholder={"Select Time Duration"}
                inputCover={"set-os-custom-cover-w"}
                value={formData["stln"]}
                isRequired={true}
                onChange={(_, durationobj) => {
                  setFormFieldData("stln")(durationobj.value);
                }}
              />
            </div>
          </div>

          <AlertsVehiclesTable key={triggerRender} headers={ALERT_VEHICLE_TABLE_HEADERS} className={"set-os-alert-table"} handleRowSelect={handleRowSelect} selectedRows={SelectedVids.current} />

          <div className="set-os-alert-submit-button-container">
            <p className="error-message">{errMsg}</p>
            <Button className="set-os-alert-button" onClick={isEdit ? saveHandler : submitHandler}>
              { isEdit ? "Save Overspeed Alerts" : "Set Overspeed Alerts"}
            </Button>
          </div>
        </form>
      </div>
      <SuccessPopupModal
        open={isOpen}
        modalMsg={"Overspeed Alert Set successfully"}
        closeBtn={"Okay"}
        modalSize={"lg"}
        onModalClose={() =>
          navigate(`${PATHS.SET_ALERTS}?tab=2`)
        }
      />
    </div>
  );
};

export default SetOverSpeedAlert;
